import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const blank = { category: "Learning & participation", status: "Doing Well", description: "", actions_taken: "",
                follow_up: "", follow_up_due: "", parent_visible: false, admin_review_required: false, reason: "" };

/** Structured, objective staff observation form (create or correct). */
export const ObservationDialog = ({ open, onOpenChange, student, existing, onSaved }) => {
  const [meta, setMeta] = useState({ categories: [], statuses: [] });
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/observations/meta").then(({ data }) => setMeta(data)); }, []);
  useEffect(() => { setF(existing ? { ...blank, ...existing, reason: "" } : blank); }, [existing, open]);

  const save = async () => {
    if (f.description.trim().length < 10) return toast.error("Describe what you observed (at least 10 characters)");
    if (existing && !f.reason.trim()) return toast.error("Please give a reason for the correction");
    setBusy(true);
    try {
      const payload = { ...f, student_id: student.id };
      ["id", "student_name", "room", "staff_email", "staff_name", "created_at", "revisions", "admin_notes",
       "resolved", "occurred_at", "attachment_path", "updated_at", "reviewed_by", "reviewed_at", "archived"]
        .forEach((k) => delete payload[k]);
      if (existing) await api.put(`/observations/${existing.id}`, payload);
      else await api.post("/observations", payload);
      toast.success(existing ? "Observation corrected" : "Observation saved");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="observation-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">
            {existing ? "Correct observation" : "New observation"} — {student?.first_name} {student?.last_name}
          </DialogTitle>
          <DialogDescription>Describe what you saw, objectively. Avoid labels; note what happened and what helped.</DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm font-bold">Category <span className="text-[#EF4444]">*</span></Label>
            <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
              <SelectTrigger className="mt-2 h-11 rounded-xl" data-testid="obs-category"><SelectValue /></SelectTrigger>
              <SelectContent>{meta.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm font-bold">Status level <span className="text-[#EF4444]">*</span></Label>
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
              <SelectTrigger className="mt-2 h-11 rounded-xl" data-testid="obs-status"><SelectValue /></SelectTrigger>
              <SelectContent>{meta.statuses.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-sm font-bold">Objective description <span className="text-[#EF4444]">*</span></Label>
            <Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })}
                      className="mt-2 rounded-xl min-h-28" data-testid="obs-description"
                      placeholder="e.g. Became upset during cleanup and needed two reminders and five minutes of individual support before transitioning." />
          </div>
          <div>
            <Label className="text-sm font-bold">Actions taken</Label>
            <Textarea value={f.actions_taken} onChange={(e) => setF({ ...f, actions_taken: e.target.value })}
                      className="mt-2 rounded-xl" data-testid="obs-actions" />
          </div>
          <div>
            <Label className="text-sm font-bold">Recommended follow-up</Label>
            <Textarea value={f.follow_up} onChange={(e) => setF({ ...f, follow_up: e.target.value })}
                      className="mt-2 rounded-xl" data-testid="obs-follow-up" />
          </div>
          <div>
            <Label className="text-sm font-bold">Follow-up due</Label>
            <Input type="date" value={f.follow_up_due} onChange={(e) => setF({ ...f, follow_up_due: e.target.value })}
                   className="mt-2 h-11 rounded-xl" data-testid="obs-follow-up-due" />
          </div>
          <div className="space-y-3 pt-7">
            <label className="flex items-center gap-3 text-sm font-semibold">
              <input type="checkbox" checked={f.parent_visible} onChange={(e) => setF({ ...f, parent_visible: e.target.checked })}
                     data-testid="obs-parent-visible" /> Family may view this entry
            </label>
            <label className="flex items-center gap-3 text-sm font-semibold">
              <input type="checkbox" checked={f.admin_review_required}
                     onChange={(e) => setF({ ...f, admin_review_required: e.target.checked })}
                     data-testid="obs-admin-review" /> Administrator review required
            </label>
          </div>
          {existing && (
            <div className="sm:col-span-2">
              <Label className="text-sm font-bold">Reason for correction <span className="text-[#EF4444]">*</span></Label>
              <Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })}
                     className="mt-2 h-11 rounded-xl" data-testid="obs-reason" />
            </div>
          )}
        </div>
        <Button onClick={save} disabled={busy} className="mt-2 w-full h-12 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold"
                data-testid="obs-save-btn">
          {busy ? "Saving…" : existing ? "Save correction" : "Save observation"}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export const STATUS_TONE = {
  "Doing Well": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Progressing": "bg-blue-50 text-blue-700 border-blue-200",
  "Needs Attention": "bg-amber-50 text-amber-700 border-amber-200",
  "Concern": "bg-orange-50 text-orange-700 border-orange-200",
  "Follow-Up Required": "bg-purple-50 text-purple-700 border-purple-200",
  "Parent Contact Recommended": "bg-pink-50 text-pink-700 border-pink-200",
  "Administrator Review Required": "bg-red-50 text-red-700 border-red-200",
};
